import React, { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Sidebar } from "primereact/sidebar";
import { Button } from "../../components/ui/Button";
import { Menu } from "primereact/menu";

import { getTitle } from "../../config/global";
import MenuList, { BrandMark } from "./MenuList";
import AvatarCircle from "../SharedComponents/AvatarCircle";
import "./css/NavTitle.css";


import { useDispatch, useSelector } from "react-redux";
import { useLogoutMutation } from "../../queries";
import { setCampaignDetails, setStepsBack, toggleDialog } from "../../redux/Slices/Global/globalSlice";
import ResetConfirmation from "../SharedComponents/ResetConfirmation";
import LanguageSwitcher from "../SharedComponents/LanguageSwitcher";
import StoragePill from "../SharedComponents/StoragePill";
import ThemeToggle from "../SharedComponents/ThemeToggle";
import EmergencyButton from "../SharedComponents/Emergency/EmergencyButton";
import { useIsRestaurant } from "../../hooks/useAccountType";
import { useInHub } from "../../nav/useInHub";

const Navbar = ({ content, title = null, date = null, icon = null, note = "", tags = "" }) => {
    const { t } = useTranslation();
    const [visibleLeft, setVisibleLeft] = useState(false);
    // New navigation: inside a hub the hub tab bar already names the page. The grey title/back-chevron strip is the
    // route-title FALLBACK (document.title, which a previous page such as عروضي leaves behind) and is not shown there;
    // a page that injects real content (toolbar, tags, publish date, note) keeps the strip for THAT content only.
    const inHub = useInHub();
    const titleData = inHub ? "" : (title == null ? getTitle() : title);
    const dateData = date == null ? "" : date;
    const { campaignDetails, openDialog, stepsBack } = useSelector((state) => state.global);
    const { playlistDetails } = useSelector((state) => state.playlist);
    const { screenDetails } = useSelector((state) => state.layout);

    const navigate = useNavigate()
    const location = useLocation();
    const isRestaurant = useIsRestaurant();
    // Restaurant users navigate the Video Wall via the WallTabs bar + sidebar +
    // each page's own hero — so the global Navbar back-chevron/title strip is just
    // redundant chrome here, and (because these pages set no title) it showed a
    // STALE document.title like "Screens" left over from the previous page. Hide it.
    const hideVideoWallBar = isRestaurant && location.pathname.startsWith('/apps/video-wall');
    const menuRef = useRef(null);
    const dispatch = useDispatch()
    const subMenuItems = [
        {
            label: t('nav.billing_settings'),
            icon: "pi pi-dollar",
            command: () => navigate("/profile", { state: { activeTab: "billing" } })
        },
        {
            label: t('nav.account_settings'),
            icon: "pi pi-user",
            command: () => navigate("/profile", { state: { activeTab: "account" } })
        },
        {
            label: t('nav.emergency_settings'),
            icon: "pi pi-bell",
            command: () => navigate("/profile", { state: { activeTab: "emergency" } })
        },
        { separator: true },
        {
            label: t('nav.logout'),
            icon: "pi pi-sign-out",
            command: () => logoutHandler(),
            className: "text-red-500"
        },
    ]
    const currentPath = window.location.pathname;

    const pagesWithWarning = ["/setup"];
    const warningPaths = [
        "setup",
        /^\/playlist\/[a-f0-9-]+\/show$/,
        /^\/apps\/animated_layouts\/[a-f0-9-]+\/show$/,
        /^\/apps\/layouts\/[a-f0-9-]+\/show$/


    ];
    const backCustomizedAction = warningPaths.some((path) =>
        typeof path === "string" ? currentPath.includes(path) : path.test(currentPath)
    );

    const handleBackClick = () => {

        if (backCustomizedAction) {
            // if (campaignDetails.status === "DRAFT" || playlistDetails?.status === "INACTIVE") {
            //     dispatch(toggleDialog("resetCampaign"))
            // } else {
            window.history.go(stepsBack);
            dispatch(setStepsBack(-1))
            // }
        } else {
            window.history.go(stepsBack);
            dispatch(setStepsBack(-1))
        }
    };

    const logout = useLogoutMutation();

    const logoutHandler = () => {
        logout.mutateAsync()
    }

    const shouldAddClass = backCustomizedAction || window.location.pathname.includes("apps/");
    // 2026-09-16: the sub-bar below renders unconditionally whenever
    // !hideVideoWallBar, even with nothing in it — Dashboard.jsx passes
    // subNavbarProps={{title:''}} specifically to blank the duplicate
    // English route-title, but blanking the TEXT alone left the row's own
    // padding/min-height (backCustomizedAction's !h-[89px], the py-2s, the
    // back-button-or-spacer div) still reserving space, showing as an empty
    // white band on mobile (desktop already fully hides via d-xl-none).
    // Compute whether there's anything real to show and skip rendering the
    // whole block when there isn't, instead of leaving an empty shell.
    //
    // 2026-10-17: a page that passes subNavbarProps={{ title: '' }} (Dashboard, Devices — the pattern those pages
    // already use to drop this bar) now ALSO drops it under /apps/, where the bare chevron strip was previously
    // forced by `pathname.includes("apps/")`. It only applies when the page supplies no content/tags/date/note,
    // so every page that renders something real in the bar is unchanged.
    const subBarOptedOut = title === '' && !(content || tags || date || note);
    const hasSubNavbarContent = inHub
        ? !!(content || tags || date || note)
        : !subBarOptedOut && !!(
            titleData || icon || date || note || tags || content ||
            backCustomizedAction || window.location.pathname.includes("apps/")
        );
    const showBackChevron = !inHub && (backCustomizedAction || window.location.pathname.includes("apps/"));
    // 2026-10-01: ac82c8a1 wrapped this whole bar in `d-xl-none` to hide a
    // generic duplicate route-title strip on desktop (titleData falling back
    // to getTitle() with nothing else real to show — e.g. Dashboard/Devices
    // passing subNavbarProps={{title:''}}). That also hid real page-supplied
    // UI a page deliberately injects here: Playlist's Publish SplitButton +
    // tag editor, Layouts/Animated Layouts' Preview+Publish buttons and the
    // ads-required `note`, Interactive Campaign's Publish SplitButton — none
    // of which are shell chrome. `content`/`tags`/`date`/`note` are always
    // real page state when set (title/icon/backCustomizedAction are route
    // chrome, not page content), so only suppress on desktop when a page
    // supplies none of them — i.e. the exact case this bar is pure fallback
    // chrome.
    const hasPageSuppliedContent = !!(content || tags || date || note);
    return (
        <>
            {/* Navbar Container — mobile/tablet top bar */}
            <nav className="z-[1040] border-bottom py-0 layout-navbar navbar bg-navbar-theme flex-wrap profile-navbar pp-mobile-navbar" id="layout-navbar ">
                <div className="container-fluid d-flex justify-content-between align-items-center py-2 profile-navbar">
                    {/* Logo */}
                    <a href="/dashboard" className="d-flex align-items-center pp-navbar-logo">
                        <BrandMark small />
                    </a>

                    {/* Right-side control cluster — explicit gaps so icons
                        don't read as one crammed blob on phones. On phones
                        (<768px) only notifications + avatar + ☰ stay here;
                        storage / theme / language collapse into the ☰ drawer. */}
                    <div className="pp-navbar-controls">
                        <EmergencyButton />

                        {/* Tablet/desktop-only controls — hidden below md and
                            relocated into the mobile menu drawer (MenuList). */}
                        <div className="pp-navbar-controls-desktop">
                            <StoragePill />
                            <ThemeToggle />
                            <LanguageSwitcher />
                        </div>

                        <AvatarCircle className="pp-navbar-avatar rounded-circle cursor-pointer" onClick={(e) => menuRef.current.toggle(e)} />

                        <Button
                            icon="fas fa-bars"
                            onClick={() => setVisibleLeft(true)}
                            className="pp-navbar-hamburger bg-transparent border-0"
                            aria-label="Open menu"
                        />
                    </div>
                </div>
            </nav >

            {/* Desktop (≥1200px) never had a top bar in the prototype — the
                sidebar brand sits level with each page's own heading. This
                sub-bar (back button / title / content slot) used to render
                unconditionally at every width; blanking the title text
                alone (last round) still left an empty strip on desktop.
                d-xl-none hid the WHOLE bar there, same breakpoint the mobile
                top bar itself already disappears at — but that also hid real
                page-supplied content/tags/date/note (Publish buttons, tag
                editor, publish-date/dirty-state lines), not just the generic
                fallback title strip it was meant to remove. Now only applied
                when the page supplies none of those (hasPageSuppliedContent
                false) — the back-button-only/generic-title case stays hidden
                on desktop exactly as before. */}
            {!hideVideoWallBar && hasSubNavbarContent && (
            <div className={`${hasPageSuppliedContent ? "" : "d-xl-none"} ${backCustomizedAction ? (note ? "!h-auto min-h-[89px]" : "!h-[89px]") : ""}  shadow-sm w-100 ${!shouldAddClass ? "py-2" : ""} d-flex flex-column bg-white pe-[1.625rem] `}>
                <div className="flex h-100 justify-center items-center py-2">

                    {showBackChevron ?
                        <>
                            {/* Back Button */}
                            <button className=" w-[50px] border-right-only h-100 mx-1 px-2 py-2" onClick={handleBackClick}>
                                <i className="fa-solid fa-chevron-left mr-1"></i>
                            </button>
                        </>
                        :
                        <div className=" mx-1 px-2 py-2"  > </div>
                    }
                    <div className="w-100">
                        <div className="col-12 flex items-center justify-between">
                            {/* Title */}
                            <div className={`relative  ml-3 my-2 mr-5 flex  justify-between flex-col nav-title  ${backCustomizedAction ? "flex-grow-1" : ""} `}>
                                <div className="flex items-center w-100">
                                    {!inHub && icon ? <div className=" mr-3"> {icon} </div> : ""}
                                    {!inHub && <span className={`  fs-4  fw-bold d-block flex-grow`} style={{ lineHeight: "24px" }}>{titleData}</span>}
                                </div>
                                {date && <div className=" my-1 text-[0.75em] ">
                                    {dateData}
                                </div>}
                                {note && (
                                    <div className="mt-1 px-2 py-1.5 bg-red-50 border border-red-200 rounded">
                                        <div className="flex items-center gap-1.5">
                                            <i className="fa-solid fa-circle-exclamation text-red-500 text-xs"></i>
                                            <small className="text-red-700 text-xs leading-tight">{note}</small>
                                        </div>
                                    </div>
                                )}
                                {tags}
                            </div >
                            <div className={`navbar-nav navbar-nav-dashboard flex-row align-items-center d-lg-flex  ${backCustomizedAction ? "" : "flex-grow-1"}`}>
                                {content}
                            </div>
                        </div>
                    </div>
                </div>


            </div >
            )}

            {/* Mobile nav drawer. `showCloseIcon={false}` + our own
                `.pp-nav-drawer-close` below replaces PrimeReact's default
                close button: that default rendered in its own absolutely-
                positioned `.p-sidebar-header` box (76x76, confirmed via
                computed-style inspection) floating over the scrim OUTSIDE
                the warm panel rather than inside it, autofocused on open
                with the base PrimeReact theme's indigo/blue focus ring
                (also confirmed — `document.activeElement` on open IS the
                close button). Rendering our own button as a normal sibling
                inside `.pp-nav-drawer-menu` instead makes it part of the
                SAME flex column as the brand/nav list/footer — pinned
                (outside `.menu-inner`'s own scroll region, see Menu.css),
                honey-colored, and never overlapping content.
                `className="pp-nav-drawer"` scopes the drawer-specific CSS
                (pp-overrides.css) to just this Sidebar instance — `.p-
                sidebar` is also used by several unrelated filter-panel
                drawers (Media/Links/VideoWall) that must keep their
                existing (un-redesigned) look. */}
            <Sidebar visible={visibleLeft} position="right" onHide={() => setVisibleLeft(false)} showCloseIcon={false} className="pp-nav-drawer">
                {/* id was "layout-menu" — duplicated the DESKTOP sidebar's
                    own id (Menu.jsx), which is always mounted in the DOM
                    at the same time (just off-canvas at mobile widths).
                    Invalid duplicate-id HTML; renamed since nothing in the
                    app queries this id by name (checked). */}
                {/* NOT `.layout-menu` — that class also carries core.css's
                    own "always off-canvas below 1200px" rule
                    (`.layout-menu{position:fixed;transform:translate3d(-100%,
                    0,0)}`, only undone by a body class this app's React
                    Layout never adds) for the DESKTOP sidebar (Menu.jsx),
                    which this drawer uses PrimeReact's own Sidebar
                    show/hide for instead — confirmed by screenshot: adding
                    that class here made this aside's entire content render
                    translated fully off-screen. `.pp-nav-drawer-menu`
                    supplies the same scroll-containment (overflow:hidden +
                    `.menu-inner` overflow-y:auto) directly, scoped to this
                    drawer only. */}
                <aside id="layout-mobile-menu" className="menu-vertical menu bg-menu-theme pp-nav-drawer-menu">
                    <button
                        type="button"
                        className="pp-nav-drawer-close"
                        aria-label={t('buttons.close')}
                        onClick={() => setVisibleLeft(false)}
                    >
                        <i className="pi pi-times" />
                    </button>
                    <MenuList />
                </aside>
            </Sidebar>
            <Menu className="z-[9999]" model={subMenuItems} popup ref={menuRef} appendTo={typeof document !== 'undefined' ? document.body : undefined} />
            {openDialog.resetCampaign ? <ResetConfirmation /> : <></>}

        </>
    );
};

export default Navbar;
